import { Clock, Star } from 'lucide-react';
import { ITimelineEvent } from '@/types/timeline';
import { Badge } from 'debug-time-machine-ui';

interface TimelineProps {
  timelineEvents: ITimelineEvent[];
  filteredEvents: ITimelineEvent[];
  currentTime: number;
  jumpToTime: (timestamp: number) => void;
  onEventSelect?: (event: ITimelineEvent) => void;
  isConnected?: boolean;
}

const Timeline = ({
  timelineEvents,
  filteredEvents,
  currentTime,
  jumpToTime,
  onEventSelect,
  isConnected = false,
}: TimelineProps) => {
  const errorCount = timelineEvents.filter(e => e.severity === 'critical' || e.isError).length;
  const warningCount = timelineEvents.filter(e => e.severity === 'warning').length;

  const getEventTypeColor = (type: string, severity?: string, isError?: boolean) => {
    if (isError || severity === 'critical') return 'bg-red-500';
    if (severity === 'warning') return 'bg-amber-500';

    switch (type) {
      case 'action':
      case 'USER_ACTION':
        return 'bg-blue-500';
      case 'state_change':
      case 'STATE_CHANGE':
        return 'bg-emerald-500';
      case 'api_call':
      case 'API_CALL':
        return 'bg-violet-500';
      case 'connection':
      case 'CONNECTION':
        return 'bg-cyan-500';
      default:
        return 'bg-slate-500';
    }
  };

  return (
    <div className='h-full flex flex-col'>
      {/* Stats Header */}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-slate-900 dark:text-white'>
            {filteredEvents.length} of {timelineEvents.length} events
          </span>
          {errorCount > 0 && (
            <Badge variant='destructive' className='text-xs h-5'>
              {errorCount}
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge variant='outline' className='text-xs h-5 border-amber-300 text-amber-700'>
              {warningCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Timeline Visualization */}
      <div className='flex-1 min-h-0'>
        {timelineEvents.length === 0 ? (
          <div className='text-center py-4 border border-dashed border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800/50 h-full flex items-center justify-center'>
            <div>
              <Clock className='w-8 h-8 text-slate-400 mx-auto mb-2' />
              <h3 className='text-sm font-medium text-slate-600 dark:text-slate-300 mb-1'>
                Ready to Debug
              </h3>
              <p className='text-xs text-slate-500 dark:text-slate-400'>
                Start interacting with your app
              </p>
            </div>
          </div>
        ) : (
          <div className='relative bg-slate-50 dark:bg-slate-800/50 rounded p-4 h-full flex flex-col justify-between'>
            {/* Timeline Bar */}
            <div className='relative bg-slate-200 dark:bg-slate-700 rounded-full h-3 mb-3'>
              {timelineEvents.map((event, index) => {
                const startTime = timelineEvents[0]?.timestamp || 0;
                const endTime =
                  timelineEvents[timelineEvents.length - 1]?.timestamp || startTime + 1;
                const timeRange = endTime - startTime || 1;
                const position =
                  timelineEvents.length === 1
                    ? 50
                    : ((event.timestamp - startTime) / timeRange) * 100;
                const isVisible = filteredEvents.some(fe => fe.id === event.id);
                const isSelected = event.timestamp === currentTime;

                return (
                  <button
                    key={event.id}
                    onClick={() => {
                      jumpToTime(event.timestamp);
                      onEventSelect?.(event);
                    }}
                    className={`
                      absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 
                      w-4 h-4 rounded-full border-2 border-white dark:border-slate-800
                      transition-all duration-200 hover:scale-125 z-10
                      ${isSelected ? 'scale-125 ring-2 ring-blue-400 ring-offset-1 dark:ring-offset-slate-800' : ''}
                      ${isVisible ? 'opacity-100' : 'opacity-40'}
                      ${getEventTypeColor(event.type, event.severity, event.isError)}
                      ${event.isError || event.severity === 'critical' ? 'animate-pulse' : ''}
                    `}
                    style={{ left: `${position}%` }}
                    title={`${new Date(event.timestamp).toLocaleTimeString()} - ${event.title}`}
                  >
                    {event.isBookmarked && (
                      <Star className='w-2 h-2 text-amber-400 fill-current absolute -top-1 -right-1' />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Timeline Labels */}
            <div className='flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-2'>
              <span>
                {timelineEvents[0]
                  ? new Date(timelineEvents[0].timestamp).toLocaleTimeString()
                  : '00:00:00'}
              </span>
              <span className='text-center'>
                {timelineEvents.length > 0 && currentTime > 0
                  ? `Event ${timelineEvents.findIndex(e => e.timestamp === currentTime) + 1}/${timelineEvents.length}`
                  : 'Click dots to navigate'}
              </span>
              <span>
                {timelineEvents.length > 0
                  ? new Date(
                      timelineEvents[timelineEvents.length - 1].timestamp
                    ).toLocaleTimeString()
                  : '00:00:00'}
              </span>
            </div>

            {/* Legend */}
            <div className='flex items-center justify-center gap-4 text-xs'>
              <div className='flex items-center gap-1'>
                <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                <span>Actions</span>
              </div>
              <div className='flex items-center gap-1'>
                <div className='w-2 h-2 bg-emerald-500 rounded-full'></div>
                <span>State</span>
              </div>
              <div className='flex items-center gap-1'>
                <div className='w-2 h-2 bg-violet-500 rounded-full'></div>
                <span>API</span>
              </div>
              <div className='flex items-center gap-1'>
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                <span>Errors</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Timeline;
